import React, { useContext } from 'react';
import { T, t } from '@sm/intl';
import { createURL } from '@sm/utils';
import { StaticContext } from '@sm/webassets/StaticContext';
import { generateMetricsAttribute } from '@sm/metrics';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';

import EndpageHeader from '~app/components/EndpageHeader';
import EndpageImage from '~app/components/EndpageImage';

import Pages, { Page } from './pages';

import useStyles from './useStyles';
import { STANDARD_ENDPAGE_DEFAULT_COPY } from './copy';
import PollingHeader from './PollingHeader';
import PollingBanner from './PollingBanner';
import { enhancePrivacyNoticeWithTracking, PrivacyNotice } from '~app/pages/SurveyThanks/utils';

type Props = {
  pageData?: Page;
  pageName: string;
  treatmentName?: string;
  utSource: string;
};

export default function PollingEndpage({
  pageData = Pages,
  pageName,
  treatmentName,
  utSource,
}: Props): React.ReactElement {
  const {
    imageSource,
    logoRoute,
    pageCTA,
    pageCtaUsage,
    pageHeading,
    pageSubheading,
    privacyNotice,
    shadedBackgroundColor,
    textBannerCTA,
    textBannerCTARoute,
    textBannerCtaUsage,
    textBannerSellPoint1 = { id: '', defaultMessage: '', description: '' },
    textBannerSellPoint2 = { id: '', defaultMessage: '', description: '' },
    textBannerSellPoint3 = { id: '', defaultMessage: '', description: '' },
    utSource2,
  } = pageData[pageName];
  const utSource3 = 'PollingEndpage';
  const { container, headingText, desktopView, mobileView, subheadingText } = useStyles({ shadedBackgroundColor });

  const textColor = shadedBackgroundColor?.startsWith('dark') ? 'light' : 'dark';

  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
    pollingSurveyId,
    cParam,
  } = useContext(StaticContext);
  const amplitudeEventName = 'endpage user interaction';

  const urlParams: Record<string, string> = {
    c: cParam,
    ut_source: utSource,
    ut_source2: utSource2,
    ut_source3: utSource3,
    ut_ctatext: pageCTA?.defaultMessage ?? '',
  };

  const pollingSurveyTokens = (pollingSurveyId as string).split('?', 2);
  const urlSurveyId = pollingSurveyTokens[0];
  if (pollingSurveyTokens.length > 1) {
    const queryString = new URLSearchParams(pollingSurveyTokens[1]);
    queryString.forEach((value, key) => {
      urlParams[key] = value;
      return true;
    });
  }
  const pollingSurveyLink = createURL(`/r/${urlSurveyId}`, urlParams);

  // Tracking parameters to privacyNotice link using the utility function
  const enhancedPrivacyNotice = enhancePrivacyNoticeWithTracking(
    privacyNotice as PrivacyNotice,
    utSource,
    utSource2,
    utSource3
  );

  return (
    <>
      <EndpageHeader
        copy={t(STANDARD_ENDPAGE_DEFAULT_COPY.ENDPAGE_HEADER)}
        hasLogo
        headerColor="sabaeus"
        headerType="thanks"
      />
      <PollingHeader
        logoRoute={logoRoute}
        utSource={utSource}
        utSource2={utSource2}
        utSource3={utSource3}
        amplitudeEventName={amplitudeEventName}
        templateType={templateType}
        surveyId={surveyId}
        collectorId={collectorId}
        respondentId={respondentId}
        shadedBackgroundColor={shadedBackgroundColor ?? null}
      />
      <Box className={container} display="flex" flexAlign="center" flexDirection="column" pt={6} px={5}>
        <Grid noBleeds noGutters>
          <Grid.Item lg={2} md={1}>
            <></>
          </Grid.Item>
          <Grid.Item lg={8} md={10} xs={12}>
            <>
              <Box mt={4}>
                <Typography align="center" color={textColor} variant="sectionTitleSm">
                  <span className={headingText}>
                    <T desc={pageHeading} html />
                  </span>
                </Typography>
              </Box>
              {pageSubheading && (
                <Box mt={2}>
                  <Typography align="center" color={textColor}>
                    <span className={subheadingText}>{t(pageSubheading)}</span>
                  </Typography>
                </Box>
              )}
            </>
          </Grid.Item>
          <Grid.Item lg={2} md={1}>
            <></>
          </Grid.Item>
        </Grid>
        <Box className={mobileView} mb={4} mt={4}>
          <EndpageImage imageSource={imageSource} />
        </Box>
        <Box mt={5} mb={5}>
          <Button
            size="lg"
            href={pollingSurveyLink}
            data-sm-metrics={generateMetricsAttribute({
              data: {
                amplitudeEvent: amplitudeEventName,
                itemSelected: pageCtaUsage,
                templateType,
                surveyId,
                collectorId,
                respondentId,
                log: {
                  message: `${utSource}-${utSource2}-${utSource3}-${pageCTA.defaultMessage}-${treatmentName}`,
                },
              },
            })}
            variant="upgrade"
          >
            {t(pageCTA)}
          </Button>
        </Box>
        <Grid noBleeds noGutters>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
          <Grid.Item lg={6} md={10} xs={12}>
            <>
              {Array.isArray(enhancedPrivacyNotice) && enhancedPrivacyNotice.length >= 2 && (
                <Box my={4} mx={4}>
                  <Typography variant="cardTitle" align="center">
                    <T desc={enhancedPrivacyNotice[0]} html values={enhancedPrivacyNotice[1]} />
                  </Typography>
                </Box>
              )}
            </>
          </Grid.Item>
          <Grid.Item lg={3} md={1}>
            <></>
          </Grid.Item>
        </Grid>
      </Box>
      <Box className={desktopView} mb={5}>
        <EndpageImage imageSource={imageSource} backgroundColor={shadedBackgroundColor} />
      </Box>
      <PollingBanner
        utSource={utSource}
        utSource2={utSource2}
        utSource3={utSource3}
        amplitudeEventName={amplitudeEventName}
        templateType={templateType}
        surveyId={surveyId}
        collectorId={collectorId}
        respondentId={respondentId}
        textBannerSellPoint1={textBannerSellPoint1}
        textBannerSellPoint2={textBannerSellPoint2}
        textBannerSellPoint3={textBannerSellPoint3}
        shadedBackgroundColor={shadedBackgroundColor ?? null}
        textBannerCTA={textBannerCTA}
        textBannerCtaUsage={textBannerCtaUsage}
        textBannerCTARoute={textBannerCTARoute}
      />
    </>
  );
}
